import {EmptyTableBody} from "../skeletonParts.tsx";
import {
    ViewModeEnum,
    type ComparisonPeriod,
    type ReportRow,
    type ViewRow,
} from "../../types";
import {SectionRows} from "./SectionRows.tsx";
import {StatementTableHeader} from "./StatementTableHeader.tsx";
import {StatementTableSkeletonBody} from "./StatementTableSkeletonBody.tsx";
import {TableFooter} from "./TableFooter.tsx";
import {AMOUNT_GROUPS, statementTableMinWidth} from "./tableStyles.ts";
import type {StatementSection} from "./types.ts";
import type {LegalEntityType} from "../../../../../generated/enums.ts";

export type {StatementSection} from "./types.ts";

interface StatementTableProps {
    sections: StatementSection[];
    viewMode: ViewModeEnum;
    legalEntityType: LegalEntityType;
    comparisonPeriod: ComparisonPeriod | null;
    balanceDiffCents: number;
    netResultRow: ReportRow | null;
    onToggleNode: (id: string) => void;
    onLeafClick: (row: ViewRow) => void;
    onKpiClick: (row: ViewRow) => void;
    loading?: boolean;
    skeletonRowCount?: number;
    showComparison?: boolean;
    emptyMessage?: string;
}

export function StatementTable({
    sections,
    viewMode,
    legalEntityType,
    comparisonPeriod,
    balanceDiffCents,
    netResultRow,
    onToggleNode,
    onLeafClick,
    onKpiClick,
    loading = false,
    skeletonRowCount = 10,
    showComparison,
    emptyMessage,
}: StatementTableProps) {
    const isSlim = viewMode === ViewModeEnum.SLIM;
    const groups = isSlim ? [] : AMOUNT_GROUPS;
    const hasComparison = showComparison ?? comparisonPeriod !== null;
    const comparisonColumns = hasComparison ? (isSlim ? 2 : 3) : 0;
    const amountColumns = (isSlim ? 1 : groups.length * 2) + comparisonColumns;
    const sectionColSpan = 1 + amountColumns;
    const minWidth = statementTableMinWidth(isSlim, groups.length, hasComparison);
    const accountColumnRowSpan = isSlim && !hasComparison ? 1 : 2;

    let body;
    if (loading) {
        body = (
            <StatementTableSkeletonBody
                sectionColSpan={sectionColSpan}
                amountColumns={amountColumns}
                skeletonRowCount={skeletonRowCount}
                isSlim={isSlim}
                hasComparison={hasComparison}
            />
        );
    } else if (sections.length === 0 || sections.every((section) => section.rows.length === 0)) {
        body = <EmptyTableBody colSpan={sectionColSpan} message={emptyMessage} />;
    } else {
        body = sections.map((section) => (
            <SectionRows
                key={section.title}
                section={section}
                groups={groups}
                isSlim={isSlim}
                hasComparison={hasComparison}
                legalEntityType={legalEntityType}
                sectionColSpan={sectionColSpan}
                balanceDiffCents={balanceDiffCents}
                onToggleNode={onToggleNode}
                onLeafClick={onLeafClick}
                onKpiClick={onKpiClick}
            />
        ));
    }

    return (
        <div
            className="relative w-full border border-gray-200 rounded-lg bg-white"
            role={loading ? "status" : undefined}
            aria-busy={loading || undefined}
        >
            {loading && <span className="sr-only">Rapport wordt geladen</span>}
            <div className="w-full overflow-x-auto rounded-t-lg">
                <table
                    className="w-full border-separate border-spacing-0 text-[13px]"
                    style={{minWidth: `${minWidth}px`}}
                >
                    <StatementTableHeader
                        isSlim={isSlim}
                        hasComparison={hasComparison}
                        comparisonPeriod={comparisonPeriod}
                        groups={groups}
                        accountColumnRowSpan={accountColumnRowSpan}
                    />
                    <tbody>{body}</tbody>
                </table>
            </div>
            {!loading && (
                <TableFooter
                    netResultRow={netResultRow}
                    legalEntityType={legalEntityType}
                    comparisonPeriod={comparisonPeriod}
                    balanceDiffCents={balanceDiffCents}
                />
            )}
        </div>
    );
}
